<!-- src/features/imprint/components/CalendarProductList.vue -->
<template>
    <div class="calendar-product-list">
        <h3 class="calendar-products__title">Selected Calendar Products</h3>
        
        <!-- Loading State -->
        <div v-if="calendarStore.isLoading" class="loading-state">
            <p>Loading calendar products...</p>
        </div>
        
        <!-- Empty State -->
        <div v-else-if="!calendarStore.hasProducts" class="empty-state">
            <p>No calendar products selected.</p>
            <p>Please select calendars from the bundle page first.</p>
        </div>
        
        <!-- Bundle Compatibility Error -->
        <template v-else>
            <p 
                v-if="calendarStore.hasBundleCompatibilityError" 
                class="message-calendar-titles"
            >
                Unfortunately, one or more calendar titles in your bundle cannot be ordered together. 
                Please remove one or more titles and try again.
            </p>
            
            <!-- Products List -->
            <div class="calendar-products">
                <div 
                    v-for="product in products" 
                    :key="product.id"
                    class="calendar-product-card"
                >
                    <!-- Product Image -->
                    <div class="product-image">
                        <img 
                            v-if="product.image"
                            :src="product.image" 
                            :alt="product.title"
                        />
                        <div v-else class="image-placeholder">
                            <span>No Image</span>
                        </div>
                    </div>
                    
                    <!-- Product Info -->
                    <div class="product-info">
                        <h4 class="product-title">{{ product.title }}</h4>
                        
                        <p v-if="product.calendar_sku" class="product-sku">
                            <span class="label">SKU:</span> 
                            <span class="value">{{ product.calendar_sku }}</span>
                        </p>
                        
                        <!-- Quantity Display (read-only) -->
                        <div class="product-quantity">
                            <span class="qty-label">Quantity:</span>
                            <span class="qty-value">{{ product.qty }}</span>
                        </div>
                        
                        <!-- Unit Price -->
                        <div v-if="product.unitPrice" class="product-price">
                            <span class="price-label">Unit Price:</span>
                            <span class="price-value">${{ formatPrice(product.unitPrice) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Total Quantity Summary -->
            <div class="quantity-summary">
                <div class="summary-row">
                    <span class="summary-label">Total Quantity:</span>
                    <span class="summary-value">{{ calendarStore.totalQuantity }}</span>
                </div>
                
                <div class="summary-row">
                    <span class="summary-label">Minimum Required:</span>
                    <span class="summary-value">{{ appStore.getMinOrderQty }}</span>
                </div>
                
                <!-- Validation Messages -->
                <div 
                    v-if="!calendarStore.meetsMinimumQuantity" 
                    class="validation-error"
                >
                    <svg class="error-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                    <span>
                        Minimum order quantity not met. 
                        Please add {{ appStore.getMinOrderQty - calendarStore.totalQuantity }} more items.
                    </span>
                </div>
                
                <div 
                    v-else-if="calendarStore.isValid && !calendarStore.hasBundleCompatibilityError"
                    class="validation-success"
                >
                    <svg class="success-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <span>Minimum quantity requirement met ✓</span>
                </div>
            </div>
        </template>
        
        <!-- Error Messages -->
        <div v-if="calendarStore.errors.length > 0" class="error-messages">
            <div v-for="(error, idx) in calendarStore.errors" :key="idx" class="error-message">
                ⚠️ {{ error }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useAppStore } from '../stores/useAppStore';
import { useCalendarStore } from '../stores/useCalendarStore';

const appStore = useAppStore();
const calendarStore = useCalendarStore();

const products = computed(() => calendarStore.productsWithQuantities);

/**
 * Format price to 2 decimal places
 */
const formatPrice = (price) => {
    return Number(price).toFixed(2);
};
</script>

<style scoped>
.calendar-product-list {
    padding: 24px 20px;
    background: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 24px;
}

.calendar-products__title {
    font-size: 20px;
    font-weight: 700;
    color: #252525;
    margin: 0 0 20px 0;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
    padding: 40px 20px;
    text-align: center;
    color: #707070;
    background: #fff;
    border-radius: 8px;
    border: 1px dashed #d7d7d7;
}

.empty-state p {
    margin: 8px 0;
}

.empty-state p:first-child {
    font-weight: 600;
    color: #252525;
}

/* Bundle Compatibility Error Message */
.message-calendar-titles {
    color: #E83223;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 16px;
    padding: 12px 16px;
    background-color: #FFF3F3;
    border: 1px solid #E83223;
    border-radius: 4px;
}

/* Products Grid */
.calendar-products {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.calendar-product-card {
    display: flex;
    gap: 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #d7d7d7;
    border-radius: 8px;
    transition: box-shadow 0.2s ease;
}

.calendar-product-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Product Image */
.product-image {
    flex-shrink: 0;
    width: 100px;
    height: 100px;
    border-radius: 6px;
    overflow: hidden;
    background: #f5f5f5;
}

.product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e9e9e9;
    color: #999;
    font-size: 12px;
}

/* Product Info */
.product-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.product-title {
    font-size: 16px;
    font-weight: 700;
    color: #252525;
    margin: 0;
    line-height: 1.3;
}

.product-sku {
    font-size: 13px;
    color: #707070;
    margin: 0;
}

.product-sku .label {
    font-weight: 600;
}

.product-sku .value {
    color: #252525;
}

.product-quantity,
.product-price {
    display: flex;
    gap: 8px;
    font-size: 14px;
    align-items: center;
}

.qty-label,
.price-label {
    color: #5a5a5a;
    font-weight: 600;
}

.qty-value {
    color: #252525;
    font-weight: 700;
    font-size: 15px;
}

.price-value {
    color: #252525;
    font-weight: 700;
}

/* Quantity Summary */
.quantity-summary {
    margin-top: 20px;
    padding: 16px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #d7d7d7;
}

.summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 15px;
}

.summary-row:last-of-type {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e9e9e9;
}

.summary-label {
    color: #5a5a5a;
    font-weight: 600;
}

.summary-value {
    color: #252525;
    font-weight: 700;
}

/* Validation Messages */
.validation-error,
.validation-success {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 6px;
    font-size: 14px;
}

.validation-error {
    background: #fff3cd;
    border: 1px solid #ffc107;
    color: #856404;
}

.validation-success {
    background: #d4edda;
    border: 1px solid #28a745;
    color: #155724;
}

.error-icon,
.success-icon {
    flex-shrink: 0;
}

/* Error Messages */
.error-messages {
    margin-top: 16px;
}

.error-message {
    padding: 12px;
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    color: #721c24;
    font-size: 14px;
    margin-bottom: 8px;
}

.error-message:last-child {
    margin-bottom: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .calendar-product-list {
        padding: 16px;
    }
    
    .calendar-product-card {
        flex-direction: column;
        gap: 12px;
    }
    
    .product-image {
        width: 100%;
        height: 200px;
    }
    
    .summary-row {
        font-size: 14px;
    }
}
</style>
